import type { LocationQueryRaw, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

/** `:name`, `:name(regex)`, `:name?`, `:name*`, `:name+` in a vue-router path pattern. */
const PARAM = /:(\w+)(\((?:[^()\\]|\\.)*\))?([?*+])?/g

export function paramNames(pattern: string): string[] {
  return [...pattern.matchAll(PARAM)].map((match) => match[1] ?? '')
}

/**
 * Fills a path pattern with values. A missing optional param drops its segment; a missing required one throws.
 * Values are URI-encoded; the result has no trailing slash (vue-router matches both forms).
 */
export function fillPath(pattern: string, values: Record<string, string | undefined>): string {
  const filled = pattern.replace(PARAM, (_whole, name: string, _regex: string | undefined, modifier?: string) => {
    const value = values[name]
    if (value === undefined || value === '') {
      if (modifier === '?' || modifier === '*') return ''
      throw new Error(`Missing a value for :${name} in ${pattern}`)
    }
    return encodeURIComponent(value)
  })
  const collapsed = filled.replace(/\/{2,}/g, '/').replace(/(.)\/$/, '$1')
  return collapsed || '/'
}

export interface RedirectSpec {
  /** The new path pattern (tests/route-manifest.json `path`). */
  to: string
  /** New param name → legacy param name, when they differ. */
  params?: Record<string, string>
  /** Query key → legacy param name: the legacy path param becomes a query parameter. */
  query?: Record<string, string>
}

function single(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value.join('/') : value
}

/**
 * The `redirect` of a generated legacy route (D-15): maps the legacy params onto the new pattern and keeps the
 * query string and the hash.
 */
export function legacyRedirect(
  spec: RedirectSpec,
): (to: Pick<RouteLocationNormalized, 'params' | 'query' | 'hash'>) => RouteLocationRaw {
  return (to) => {
    const values: Record<string, string | undefined> = {}
    for (const name of paramNames(spec.to)) values[name] = single(to.params[spec.params?.[name] ?? name])
    const query: LocationQueryRaw = { ...to.query }
    for (const [key, from] of Object.entries(spec.query ?? {})) {
      const value = single(to.params[from])
      if (value !== undefined && value !== '') query[key] = value
    }
    return { path: fillPath(spec.to, values), query, hash: to.hash }
  }
}
